// src/components/ManageDealDrawer/useManageDealDrawer.ts

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './ManageDealDrawer.module.scss';
import { isEqual } from 'lodash';

// Redux hooks & actions (replace paths with your own)
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
    createDeal,
    createInvestmentPreference,
    getDealData,
    getInvestmentPreference,
    getLenderDealData,
    getMetricsForDealCreation,
    setDealDrawer,
    updateCompany,
    updateInvestmentPreference,
} from '../../store/investor/action';
import {
    fetchAllAvailableProducts,
    fetchCompanySectorsList,
    getDealFormConfig,
    getWebsiteVerification,
} from '../../store/investor/apiHelpers';
import { getCompanyDealMetrics, getCompanyMetrics, updateCompanyMetrics } from '../../store/debtProducts/apiHelper_debtProducts';

// Utilities (replace paths with your own)
import {
    convertToFirstLetterCapital,
    flatten,
    getPlatformUserId,
    getSubdomain,
    isEmpty,
    isLenderType,
    linkedInRegex,
    unflatten,
} from '../../utils/utils';
import { getAuditedYear, getFinancialYear, getTimeStamp, initializeDatepickerDates } from '../../utils/dateUtils';
import { updateQueryParams } from '../../utils/searchParamUtils';
import { Mixpanel } from '../../utils/mixpanel';
import { useWindowUnloadEffect } from '../../utils/constants/hooks';
import { EstimateButtonIcon } from '../../assets/Svg/EstimateButtonIcon';

// Constants
import {
    AICA_COMPANY_PROFILE,
    AICA_CREATE_DEAL_DETAILS,
    AICA_GET_COMPANY_SECTOR_LIST,
    AICA_INVESTMENT_PREFERENCE,
    AICA_PARTNER_PROFILE,
    AICA_YOUR_PROFILE,
    CONFIG_STEP,
    CREATE_INVESTMENT_PREFERENCE,
    DATA_SOURCE_TYPE,
    DEAL_DRAWER_ICON_MAP,
    DEBT_PRODUCT_FILTERS,
    DEFAULT_OWNER_TYPE,
    FY_END_DATE,
    METADATA_KEYS_FOR_BORROWER_FINANCIALS,
    ORG_USERS_TABLE,
    PEC_STATUS,
    PROFILE,
    PROFILE_BUTTON,
    PROFILE_CUSTOM_FIELD_KEYS,
    USER_TYPE,
} from './DealDrawerConstants';
import {
    buildFilteredSectionConfig,
    buildPickerOptions,
    expandFieldKeys,
    getAlwaysOnFieldKeys,
    resolveSelectedFields,
    selectionStorageKey,
} from './pecFieldSelection';
import { fetchOrgInfoById } from '../../store/investor/investorApiHelper';
import { setDebtProductsFilters } from '../../store/company/action';
import { AUDITED_FINANCIAL_KEY_METRIC } from '../../modules/DebtProducts/DebtProductsConstants';
import { fetchDataByConfig, fetchAllLendersWithConnections } from './ManageDealDrawerService';
import { convertToWordsWithUnit } from '../../utils/numberToWordUtil';
import { USER_TYPES } from '../../store/investor/constants';
import { polusBaseUrl } from '../../utils/axios-interceptor';

interface UseManageDealDrawerProps {
    source: string;
    onClose?: Function;
    dealId?: string;
    saveSelection?: Function;
    fetchCompanyMetrics?: Function;
    dealStatus?: string;
}

export function useManageDealDrawer({ source, onClose, dealId, saveSelection, fetchCompanyMetrics, dealStatus }: UseManageDealDrawerProps) {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const urlParams = useParams();
    const [params, setParams] = useSearchParams();

    // Redux states
    const investorData = useAppSelector((state) => state.investor);
    const loggedInUser = useAppSelector((state) => state.user.userData);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const user = useAppSelector((state) => state.user);
    const userData = user?.userData;

    // Local states
    const [formConfig, setFormConfig] = useState<any>(null);
    const [stepperConfig, setStepperConfig] = useState<any>(null);
    const [formData, setFormData] = useState<{ [key: string]: any }>({});
    const [initialFormData, setInitialFormData] = useState<{ [key: string]: any }>({});
    const [step, setStep] = useState<number>(investorData?.dealDrawer?.selectedStep ?? 1);
    const [disablePrimary, setDisablePrimary] = useState<boolean>(true);
    const [disableSecondary, setDisableSecondary] = useState<boolean>(step === 1);
    const [lenderList, setLenderList] = useState<{ id: string; name: string }[]>([]);
    const [preFilledDataFetched, setPreFilledDataFetched] = useState<boolean>(false);
    const [loadingPrefilledData, setLoadingPrefilledData] = useState<boolean>(false);
    const [animationClass, setAnimationClass] = useState<string>('');
    const [investorName, setInvestorName] = useState('');
    const [displayOrgName, setDisplayOrgName] = useState('');
    const [investmentPreferenceId, setInvestmentPreferenceId] = useState<string | null>(null);
    const [isFromComplete, setIsFromComplete] = useState<boolean>(false);
    const [restrictedMap, setRestrictedMap] = useState<Map<string, boolean>>(new Map());
    const [loanTypeOptions, setLoanTypeOptions] = useState<any>({});
    const [loanTypeNameToIdMap, setLoanTypeNameToIdMap] = useState(new Map<string, string>());
    const [auditedfinancialsMapping, setAuditedfinancialsMapping] = useState<Record<string, { value: string | number | null }>>({});
    const debtProductFilters = useAppSelector((state) => state.company.debtProductsFilters);
    const [duplicate, setDuplicate] = useState(false);
    const [filterType, setFilterType] = useState('');
    const [sectorList, setSectorList] = useState<any[]>([]);
    const [negativeSectorList, setNegativeSectorList] = useState<any[]>([]);
    const [isWebsiteValid, setIsWebsiteValid] = useState(true);
    const [isLinkedInValid, setIsLinkedInValid] = useState(true);
    const [isWebsiteVerificationLoading, setIsWebsiteVerificationLoading] = useState(false);
    const [isWebsitePrefilled, setIsWebsitePrefilled] = useState(false);
    const [negativeLenderList, setNegativeLenderList] = useState<any[]>([]);
    const [connectedLenderNames, setConnectedLenderNames] = useState<string[]>([]);
    const [activeFinancialsTab, setActiveFinancialsTab] = useState<string>('');

    // Dismissal alert state
    const [showDismissalAlert, setShowDismissalAlert] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [originalFormData, setOriginalFormData] = useState<{ [key: string]: any }>({});
    const [isFormInitialized, setIsFormInitialized] = useState(false);

    const [citiesList, SetCitiesList] = useState<{ label: string; value: string }[]>([]);
    const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);
    const [isPickerOpen, setIsPickerOpen] = useState(false);

    const subDomain = getSubdomain();
    const userOrgId = sessionStorage.getItem('user_org_id') ?? localStorage.getItem('user_org_id') ?? '';
    const isLender = isLenderType(userData?.accountType);
    const isPartner = user.userData.accountType === USER_TYPE.PARTNER;
    const flowId = 16;
    const formDataRef = useRef<{}>({});

    const BORROWER_PROFILE_CONSTANTS = {
        WEBSITE: 'borrowerProfile.website',
        LINKEDIN: 'borrowerProfile.linkedin',
    };
    const UNIT_CONSTANTS = {
        INR_CR: 'INR CR',
    };

    // Refs
    const dealDrawerBodyRef = useRef<any>(null);
    const stepContainerRef = useRef<any>(null);

    // -------------------------------------
    // REUSABLE FUNCTIONS / SIDE EFFECTS
    // -------------------------------------

    // 1. Create a map of required form keys by scanning the entire `formConfig`.
    const createMapOfRequiredVsSectionKeys = () => {
        const requiredMap = new Map<string, boolean>();
        if (!formConfig) return;

        for (const currentStepConfig of formConfig) {
            for (const config of currentStepConfig.config) {
                const sectionKey = config?.sectionKey;
                for (const key in config?.config) {
                    const fieldConfig = config.config[key];
                    let formKey = sectionKey ? `${sectionKey}.${key}` : key;

                    if (fieldConfig.isRequired) {
                        if (fieldConfig.type === 'range') {
                            let minKey = `minimum${convertToFirstLetterCapital(key)}`;
                            let maxKey = `maximum${convertToFirstLetterCapital(key)}`;
                            if (sectionKey) {
                                minKey = `${sectionKey}.${minKey}`;
                                maxKey = `${sectionKey}.${maxKey}`;
                            }
                            requiredMap.set(minKey, true);
                            requiredMap.set(maxKey, true);
                        } else {
                            requiredMap.set(formKey, true);
                        }
                    }
                }
            }
        }
        setRestrictedMap(requiredMap);
    };

    const getConvertedValue = (value: string, unit: string) => {
        if (!value) return '';
        let convertedWord = '';
        switch (unit) {
            case UNIT_CONSTANTS.INR_CR:
                convertedWord = convertToWordsWithUnit(Number(value), 'CR');
                break;
            default:
                break;
        }
        return convertedWord;
    };

    // 2. Check if the entire form is complete by comparing `formData` with `restrictedMap`.
    const checkCompleteForm = () => {
        let isValid = true;
        for (const [key] of restrictedMap) {
            if (!formData[key]) {
                isValid = false;
                break;
            }
        }
        setIsFromComplete(isValid);
    };

    const validateWebsite = async (url: string) => {
        if (!url || step != 2) return;
        setDisablePrimary(true);
        setDisableSecondary(true);
        setIsWebsiteVerificationLoading(true);
        const payload = {
            websiteUrl: url,
            skipCin: true,
        };
        const response = await getWebsiteVerification(payload);
        if (response?.redirectDomain) {
            setIsWebsiteValid(true);
        } else {
            setIsWebsiteValid(false);
        }
        setDisablePrimary(!response.redirectDomain || !isLinkedInValid);
        setDisableSecondary(!response.redirectDomain || !isLinkedInValid);
        setIsWebsiteVerificationLoading(false);
    };

    // 3. Validate the current step (enable/disable primary button).
    const validateForm = () => {
        if (!formConfig) return;
        const currentStepConfig = formConfig?.[step - 1];
        if (!currentStepConfig) return;

        const currentStepKeys: string[] = [];
        for (const config of currentStepConfig?.config ?? []) {
            const sectionKey = config?.sectionKey;
            for (const key in config.config) {
                const fieldConfig = config.config[key];
                if (!fieldConfig.isRequired) continue;

                let formKey = sectionKey ? `${sectionKey}.${key}` : key;

                // if it's a range
                if (fieldConfig.type === 'range') {
                    let minKey = `minimum${convertToFirstLetterCapital(key)}`;
                    let maxKey = `maximum${convertToFirstLetterCapital(key)}`;
                    let importanceKey = `importance${convertToFirstLetterCapital(key)}`;
                    if (sectionKey) {
                        minKey = `${sectionKey}.${minKey}`;
                        maxKey = `${sectionKey}.${maxKey}`;
                        importanceKey = `${sectionKey}.${importanceKey}`;
                    }
                    currentStepKeys.push(minKey, maxKey);
                    if (fieldConfig.importance) {
                        currentStepKeys.push(importanceKey);
                    }
                }
                // if there's "importance"
                else if (fieldConfig.importance) {
                    let importanceKey = `importance${convertToFirstLetterCapital(key)}`;
                    if (sectionKey) {
                        importanceKey = `${sectionKey}.${importanceKey}`;
                    }
                    currentStepKeys.push(formKey, importanceKey);
                } else {
                    currentStepKeys.push(formKey);
                }
            }
        }
        // all values must be present in formData
        const isFormValid = currentStepKeys.every((key) => key in formData && !isEmpty(formData[key])) && isWebsiteValid && isLinkedInValid;
        setDisablePrimary(!isFormValid);
        setDisableSecondary(!isFormValid);
    };

    useEffect(() => {
        if (isWebsitePrefilled) {
            setIsWebsiteValid(true);
        } else {
            setIsWebsiteValid(false);
        }
    }, [formData[BORROWER_PROFILE_CONSTANTS.WEBSITE]]);

    useEffect(() => {
        validateForm();
    }, [isWebsiteValid]);

    useEffect(() => {
        if (formData[BORROWER_PROFILE_CONSTANTS.WEBSITE]) {
            setIsWebsitePrefilled(true);
        }
    }, [step]);

    const getButtonText = (): string => {
        if (isWebsiteVerificationLoading) return '';
        if (isWebsiteValid) {
            return formData[BORROWER_PROFILE_CONSTANTS.WEBSITE] ? 'Verified' : 'Verify';
        }
        return formData[BORROWER_PROFILE_CONSTANTS.WEBSITE] ? 'Verify' : '';
    };

    const getButtonIcon = (buttonImgName: string): JSX.Element => {
        if (buttonImgName === 'VERIFY_WEBSITE') {
            if (isWebsiteVerificationLoading) {
                return React.cloneElement(DEAL_DRAWER_ICON_MAP.VERIFY_WEBSITE.LOADING, {
                    className: styles.customFetch,
                });
            }
            if ((isWebsiteValid && isEmpty(formData[BORROWER_PROFILE_CONSTANTS.WEBSITE])) || !isWebsiteValid) {
                return React.cloneElement(DEAL_DRAWER_ICON_MAP.VERIFY_WEBSITE.INVALID, {
                    className: styles.customFetch,
                });
            }
            return React.cloneElement(DEAL_DRAWER_ICON_MAP.VERIFY_WEBSITE.VALID, {
                className: styles.customFetch,
            });
        }
        const icon = DEAL_DRAWER_ICON_MAP[buttonImgName];
        if (typeof icon === 'string') {
            return <img src={icon} width={'14'} height={'14'} />;
        }
        if (React.isValidElement(icon)) {
            return React.cloneElement(icon);
        }
        return <></>;
    };

    // 4. Close the drawer
    const closeDrawer = (payload?: any) => {
        params.delete('investmentPreferenceId');
        params.delete('Duplicate');
        updateQueryParams(params, setParams, {});
        dispatch(setDealDrawer({ open: false }));
        if (source === DEBT_PRODUCT_FILTERS) {
            params.set('filters', 'true');
            updateQueryParams(params, setParams, {});
            onClose?.(payload ?? debtProductFilters);
        } else {
            onClose?.(debtProductFilters);
        }
    };

    const handleDrawerClose = () => {
        if (hasUnsavedChanges) setShowDismissalAlert(true);
        else closeDrawer();
    };

    const handleSaveAndClose = async () => {
        setShowDismissalAlert(false);
        await handlePrimary();
        closeDrawer();
    };

    const handleCloseWithoutSave = () => {
        setShowDismissalAlert(false);
        closeDrawer();
    };

    const hasFinancialDataInNestedObject = (obj: any): boolean => {
        if (!obj || typeof obj !== 'object') return false;
        return Object.keys(obj)
            .filter((key) => !METADATA_KEYS_FOR_BORROWER_FINANCIALS.includes(key))
            .some((key) => {
                const value = obj[key];
                return value !== '' && value !== null && value !== undefined;
            });
    };

    // 5. Generic handle change
    const handleChange = (key: string, value: any, importanceKey?: string | null) => {
        // in case the value is empty we need to delete the key
        if (dealStatus === 'DRAFT_DELETED' && !params.has('draftDeleteUpdated')) {
            updateQueryParams(params, setParams, { draftDeleteUpdated: true }, location);
        }

        if (value === '' || value === null) {
            setFormData((prev) => {
                const updatedData = { ...prev };
                delete updatedData[key];
                importanceKey && delete updatedData[importanceKey];
                return updatedData;
            });
        } else {
            setFormData((prev) => ({ ...prev, [key]: value }));
        }
    };

    // -------------------------------------
    // 5a. PEC per-step field selection (the picker-controlled render set)
    // -------------------------------------
    const isPec = !!investorData?.dealDrawer?.isPec;
    const currentStepSections = useMemo(() => formConfig?.[step - 1]?.config ?? [], [formConfig, step]);

    // A PEC page carries exactly one section with a selection storage key; any other section on
    // the page keeps rendering unfiltered.
    const pecStepSection = useMemo(
        () => (isPec ? currentStepSections.find((section: any) => !!selectionStorageKey(section?.sectionKey)) ?? null : null),
        [isPec, currentStepSections],
    );
    const pecStorageKey = selectionStorageKey(pecStepSection?.sectionKey);
    const storedStepSelection = pecStorageKey ? formData[pecStorageKey] : undefined;

    const pecAlwaysOnFields = useMemo(() => (pecStepSection ? getAlwaysOnFieldKeys(pecStepSection) : []), [pecStepSection]);

    // Keyed on the stored array's identity, never on formData: the selection only changes when
    // that array is replaced, and a formData-keyed memo would hand FormSection a fresh section
    // object — and so re-run its validation effect — on every keystroke.
    const pecSelectedFields = useMemo(
        () => (pecStepSection ? resolveSelectedFields(pecStepSection, formData) : []),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pecStepSection, storedStepSelection],
    );

    const pecPickerOptions = useMemo(() => (pecStepSection ? buildPickerOptions(pecStepSection) : []), [pecStepSection]);

    const pecFilteredSection = useMemo(
        () => (pecStepSection ? buildFilteredSectionConfig(pecStepSection, pecSelectedFields, pecAlwaysOnFields) : null),
        [pecStepSection, pecSelectedFields, pecAlwaysOnFields],
    );

    // Always-on fields keep a step non-empty, so the empty state is reachable only where the
    // config declares no required field.
    const isStepConfigured = !pecStepSection || pecSelectedFields.length + pecAlwaysOnFields.length > 0;

    const expandStepFieldKeys = (fieldKey: string): string[] =>
        pecStepSection ? expandFieldKeys(pecStepSection.sectionKey ?? '', fieldKey, pecStepSection.config?.[fieldKey]) : [];

    const pecRemovedValuesStashRef = useRef<Record<string, Record<string, any>>>({});

    // Dropping a field's keys from formData is what produces its null tombstones on save: keys
    // that never held a value are absent from initialFormData, so no tombstone is invented for them.
    const applyStepSelection = (nextSelection: string[], fieldsToClear: string[], fieldsToRestore: string[] = []) => {
        if (!pecStorageKey) return;
        const stash = pecRemovedValuesStashRef.current;
        fieldsToClear.forEach((fieldKey) => {
            const values: Record<string, any> = {};
            expandStepFieldKeys(fieldKey).forEach((key) => {
                if (formData[key] !== undefined) values[key] = formData[key];
            });
            if (Object.keys(values).length) stash[`${pecStorageKey}|${fieldKey}`] = values;
        });
        const restores: Record<string, any> = {};
        fieldsToRestore.forEach((fieldKey) => {
            const values = stash[`${pecStorageKey}|${fieldKey}`];
            if (!values) return;
            Object.assign(restores, values);
            delete stash[`${pecStorageKey}|${fieldKey}`];
        });
        const keysToClear = fieldsToClear.flatMap(expandStepFieldKeys);
        setFormData((prev) => {
            const updatedData = { ...prev };
            keysToClear.forEach((key) => delete updatedData[key]);
            Object.entries(restores).forEach(([key, value]) => {
                if (updatedData[key] === undefined) updatedData[key] = value;
            });
            updatedData[pecStorageKey] = nextSelection;
            return updatedData;
        });
    };

    // The picker applies immediately: every tick replaces the step's selection. Unticking a
    // field removes it (values stashed); re-ticking it in the same session restores them.
    const handlePickerSelectionChange = (nextSelection: string[]) => {
        const cleanNext = nextSelection.filter((fieldKey) => !pecAlwaysOnFields.includes(fieldKey));
        applyStepSelection(
            cleanNext,
            pecSelectedFields.filter((fieldKey) => !nextSelection.includes(fieldKey)),
            cleanNext.filter((fieldKey) => !pecSelectedFields.includes(fieldKey)),
        );
    };

    const handleClearField = (fieldKey: string) => {
        const keysToClear = expandStepFieldKeys(fieldKey);
        if (!keysToClear.length) return;
        setFormData((prev) => {
            const updatedData = { ...prev };
            keysToClear.forEach((key) => delete updatedData[key]);
            return updatedData;
        });
    };

    const handleRemoveAllForStep = () => applyStepSelection([], pecSelectedFields);

    // Clear All keeps the rows on screen and only blanks their values — always-on fields
    const handleClearAllForStep = () => {
        const keysToClear = [...pecSelectedFields, ...pecAlwaysOnFields].flatMap(expandStepFieldKeys);
        if (!keysToClear.length) return;
        setFormData((prev) => {
            const updatedData = { ...prev };
            keysToClear.forEach((key) => delete updatedData[key]);
            return updatedData;
        });
    };

    // Seed the resolved selection into formData once per opened PEC, before the 500ms
    // originalFormData snapshot, so the drawer does not open already claiming unsaved changes.
    const pecSelectionSeededRef = useRef(false);
    useEffect(() => {
        if (!isPec || !formConfig || pecSelectionSeededRef.current) return;
        const prefId = investmentPreferenceId || params.get('investmentPreferenceId');
        // An existing PEC's selection may have to be derived from its saved values — wait for them.
        if (prefId && isEmpty(initialFormData)) return;
        pecSelectionSeededRef.current = true;
        setFormData((prev) => {
            const updatedData = { ...prev };
            for (const page of formConfig) {
                for (const section of page?.config ?? []) {
                    const storageKey = selectionStorageKey(section?.sectionKey);
                    if (!storageKey) continue;
                    updatedData[storageKey] = resolveSelectedFields(section, prev);
                }
            }
            return updatedData;
        });
    }, [isPec, formConfig, initialFormData, investmentPreferenceId, params]);

    // Handle tab change for borrower financials (kept for backward compatibility)
    const handleFinancialsTabChange = (tab: string) => {
        if (!tab || tab === activeFinancialsTab) return;
        setActiveFinancialsTab(tab);
    };

    // 6. Move to next / previous step
    const navigateStep = (newStep: number, animation: string) => {
        setAnimationClass(animation);
        setTimeout(() => {
            setStep(newStep);
        }, 300);
    };

    const continueToNextStep = () => {
        if (step === formConfig?.length) {
            onClose?.();
        } else {
            navigateStep(step + 1, 'slide-down');
        }
    };

    const flowToMandate = (currentFormData: any, initialFormData: any): boolean => {
        const keysToCompare = ['dealTerms.maximumDealAmount', 'dealTerms.roi', 'dealTerms.tenure', 'dealTerms.securityOffered'];

        return keysToCompare.some((key) => currentFormData[key] !== initialFormData[key]);
    };

    const handlePrimary = async () => {
        if (source === DEBT_PRODUCT_FILTERS) {
            let payload: any = unflatten(formData);
            if (!isEmpty(payload)) payload.filters.filterType = filterType;
            dispatch(setDebtProductsFilters(payload));
            closeDrawer(payload);
            return;
        }
        if (source === 'Debt Products') {
            if (step === 1 && dealId && dealStatus === 'DRAFT' && flowToMandate(formData, formDataRef.current as Record<string, any>)) {
                updateQueryParams(params, setParams, { updateMandate: true }, location);
                return;
            }
            // Update metrics, then navigate - await the API call
            await companyMetricsUpdate(() => {
                if (step === formConfig?.length) {
                    closeDrawer();
                } else {
                    navigateStep(step + 1, 'slide-down');
                }
            });
            return;
        } else if (source === PROFILE_BUTTON || source === ORG_USERS_TABLE) {
            // Persist custom fields to OrganisationMaster (separate from metrics)
            await persistProfileCustomFields();

            // Update metrics, then navigate - await the API call
            await companyMetricsUpdate(() => {
                if (step === formConfig?.length) {
                    closeDrawer();
                } else {
                    navigateStep(step + 1, 'slide-down');
                }
            });
            return;
        }

        if (investorData?.dealDrawer?.isPec) {
            // create or update PEC
            createOrUpdatePEC(() => navigateStep(step + 1, 'slide-down'), false);
            return;
        }

        // normal "Create Deal" flow
        const currentConf = formConfig?.[step - 1];
        if (currentConf?.step === CONFIG_STEP.SELECT_INVESTORS) {
            handleCreateDeal();
            return;
        }

        if (step + 1 > formConfig.length) return;
        navigateStep(step + 1, 'slide-down');
    };

    const fetchNegativeLenders = async () => {
        try {
            if (userData?.investorOrgId) {
                const assesseeOrgId =
                    (urlParams.id as string) || user.userData.assesseeOrgId || (dealDrawerState?.companyId as string) || '';
                const response = await fetchAllLendersWithConnections(userData.investorOrgId, assesseeOrgId || undefined);
                const negativeLenderObj = response?.data?.lenderLegalNameIdsMap?.ADMIN;
                if (negativeLenderObj) {
                    let options: any = [];
                    Object.entries(negativeLenderObj).forEach(([lenderName, ids]) => {
                        options.push({
                            label: lenderName,
                            value: ids,
                        });
                    });
                    setNegativeLenderList(options);
                }
                const connectedNames: string[] = response?.data?.connectedLenderNames || [];
                setConnectedLenderNames(connectedNames);
            }
        } catch (error) {
            console.error('Error fetching Negative Lender List:', error);
        }
    };

    const handleSecondary = () => {
        if (source === DEBT_PRODUCT_FILTERS) {
            setFormData({});
            setOriginalFormData({});
            dispatch(setDebtProductsFilters({}));
        }
        if (step <= 1) return;
        navigateStep(step - 1, 'slide-up');
    };

    const handleStepperClick = (index: number) => {
        const direction = index > step ? 'slide-up' : 'slide-down';
        if (investorData?.dealDrawer?.isPec) {
            createOrUpdatePEC(() => navigateStep(index, direction), true);
        } else {
            navigateStep(index, direction);
        }
    };

    const handleUpdateMandate = async () => {
        try {
            await companyMetricsUpdate(() => {});

            let latestMetricsData = {};
            if (fetchCompanyMetrics) {
                latestMetricsData = await fetchCompanyMetrics(false, true);
            }

            if (saveSelection) {
                saveSelection('DRAFT', dealId, '', latestMetricsData);
            }
            continueToNextStep();

            params.delete('updateMandate');
            setParams(params);
        } catch (e) {
            console.error('Update failed', e);
        }
    };
    const fetchCitiesList = async () => {
        try {
            if (!userData?.investorOrgId) {
                return;
            }

            const response = await fetchDataByConfig(
                {
                    parentOrgId: userData.investorOrgId,
                },
                'GET_ALL_CITIES',
                'GET',
                polusBaseUrl,
            );

            const citiesArr = response?.data?.responseBody?.AICA_GET_CITIES_LIST?.cities;
            if (Array.isArray(citiesArr)) {
                const cityOptions = citiesArr.map((city) => ({ label: city, value: city }));

                SetCitiesList(cityOptions);
            } else {
                SetCitiesList([]);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            SetCitiesList([]);
        }
    };

    const handleSkipMandate = async () => {
        try {
            await companyMetricsUpdate(() => continueToNextStep());
            params.delete('updateMandate');
            setParams(params);
        } catch (e) {
            console.error('Skip failed', e);
        }
    };

    // 7. Create or Update "PEC"
    const createOrUpdatePEC = (navigationFunction: () => void, isStepper: boolean) => {
        setLoadingPrefilledData(true);

        const actualUser = JSON.parse(localStorage.getItem('user_data') ?? '{}')?.userName;

        // Important identifiers that should always be included
        const basePayload = {
            aggregatorId: platformData?.investorOrgId,
            lenderId: userOrgId,
            activityBy: actualUser ?? loggedInUser?.userName,
            type:
                userData?.accountType === USER_TYPE.INVESTOR
                    ? USER_TYPE.ADMIN
                    : isLenderType(userData?.accountType)
                    ? USER_TYPE.LENDER
                    : userData?.accountType,
            status: isFromComplete ? 'COMPLETE' : 'PENDING',
        };

        const isUpdate =
            (investmentPreferenceId && !params.has('Duplicate')) ||
            ((source === PROFILE_BUTTON || source === ORG_USERS_TABLE) && formData?.id);

        // Get loan types regardless of changes since it's required
        const unflattened = unflatten(formData) as { preferredTerms?: { loanTypes?: string[] } };
        const currentProducts = unflattened?.preferredTerms?.loanTypes
            ?.map((productName: string) => loanTypeNameToIdMap.get(productName))
            ?.filter(Boolean);

        if (!currentProducts?.length) {
            setLoadingPrefilledData(false);
            // For create/Duplicate flow: allow navigation to next step when we're before Preferred Terms
            // (user needs to reach that step to select loan types). Only require products when we're on or past that step.
            const preferredTermsStepIndex = formConfig?.findIndex((c: any) => c?.step === CONFIG_STEP.PREFERRED_TERMS);
            const isBeforePreferredTermsStep = preferredTermsStepIndex >= 0 && step - 1 < preferredTermsStepIndex;
            if (!isUpdate && isBeforePreferredTermsStep) {
                navigationFunction();
                return;
            }
            toast.error('Please select at least one loan type');
            return;
        }

        if (isUpdate) {
            // Get changed data by comparing with initial form data
            const changedData: { [key: string]: any } = {};
            // ➊ Updated / added keys
            Object.keys(formData).forEach((key) => {
                if (!isEqual(formData[key], initialFormData[key])) {
                    changedData[key] = formData[key];
                }
            });

            // ➋ Deleted keys
            Object.keys(initialFormData).forEach((key) => {
                if (!(key in formData)) {
                    changedData[key] = null; // or `undefined` depending on API contract
                }
            });

            // If no changes and not a stepper navigation, just navigate
            if (Object.keys(changedData).length === 0) {
                setLoadingPrefilledData(false);
                if (!isStepper && formConfig?.[step - 1]?.ctas?.primary?.text === 'Save & Close') {
                    closeDrawer();
                    return;
                }
                navigationFunction();
                return;
            }

            // Create payload with only changed data while preserving structure
            const payload = {
                ...basePayload,
                investmentPreferenceId,
                products: currentProducts, // Always include products
                ...unflatten(changedData, { includeNull: true }), // Include only changed data
                isActive: formData?.isActive,
            };

            Mixpanel.track('PEC Edited', {
                userEmail: userData?.primaryEmail,
                PNOId: userData?.investorOrgId,
                PECId: investmentPreferenceId,
                LoanTypes: payload.products,
                MetricsUpdated: changedData, // Track only changed fields
                UpdatedBy: userData?.userName,
                LastUpdated: new Date().toISOString(),
            });

            dispatch(
                updateInvestmentPreference(payload, {
                    onSuccess: () => {
                        setLoadingPrefilledData(false);
                        setInitialFormData(formData); // Update initial form data after successful update
                        setOriginalFormData(formData);
                        if (!isStepper && formConfig?.[step - 1]?.ctas?.primary?.text === 'Save & Close') {
                            closeDrawer();
                            const message = getToastMessages(source);
                            toast.success(message.success);
                            return;
                        }
                        navigationFunction();
                    },
                    onError: () => {
                        setLoadingPrefilledData(false);
                        const message = getToastMessages(source);
                        toast.error(message.error);
                    },
                }),
            );
        } else {
            // For new creation, include all data
            // Snapshot what is actually sent: the create response arrives in a closure that must
            // not read a later formData.
            const sentFormData = { ...formData };
            const payload = {
                ...basePayload,
                ...unflatten(formData, { includeNull: true }),
                products: currentProducts,
                investmentPreferenceType: source === PROFILE_BUTTON || source === ORG_USERS_TABLE ? PROFILE : PEC_STATUS.PEC,
                isActive: formData?.isActive,
            };

            // Track appropriate event
            if (params.has('Duplicate')) {
                Mixpanel.track(PEC_STATUS.PEC_DUPLICATED, {
                    userEmail: userData?.primaryEmail,
                    PNOId: userData?.investorOrgId,
                    LoanTypes: payload.products,
                    DuplicatedBy: userData?.userName,
                    DuplicationTime: new Date().toISOString(),
                });
            } else {
                Mixpanel.track(PEC_STATUS.PEC_CREATED, {
                    userEmail: userData?.primaryEmail,
                    PNOId: userData?.investorOrgId,
                    PNOName: subDomain,
                    LoanTypes: payload.products,
                    CreationTime: new Date().toISOString(),
                });
            }

            dispatch(
                createInvestmentPreference(payload, {
                    onSuccess: (res) => {
                        // Without a baseline, a removal later in the same session is undetectable —
                        // deletions are computed against initialFormData.
                        setInitialFormData(sentFormData);
                        setOriginalFormData(sentFormData);
                        if (source === PROFILE_BUTTON || source === ORG_USERS_TABLE) {
                            setLoadingPrefilledData(false);
                            closeDrawer();
                            const messages = getToastMessages(source);
                            toast.success(messages.success);
                            return;
                        }
                        params.set('investmentPreferenceId', res?.investmentPreferenceId);
                        setParams(params);
                        if (params.has('Duplicate')) {
                            params.delete('Duplicate');
                            updateQueryParams(params, setParams, {});
                        }
                        setInvestmentPreferenceId(res?.investmentPreferenceId);
                        navigateStep(step + 1, 'slide-down');
                        setLoadingPrefilledData(false);
                    },
                    onError: () => {
                        setLoadingPrefilledData(false);
                        const messages = getToastMessages(CREATE_INVESTMENT_PREFERENCE);
                        toast.error(messages.error);
                    },
                }),
            );
        }
    };

    // 8a. Persist profile custom fields to OrganisationMaster
    const persistProfileCustomFields = (): Promise<void> => {
        const unflattened: any = unflatten(formData);
        const profileData = unflattened?.profile || {};
        const customFieldsPayload: Record<string, any> = {};
        // Always include all keys so cleared fields (deleted by handleChange) also reach the server
        PROFILE_CUSTOM_FIELD_KEYS.forEach((key) => {
            customFieldsPayload[key] = profileData[key] !== undefined ? profileData[key] : '';
        });
        const orgId = params.get('orgId') || (isPartner && userOrgId) || (isLender ? userData?.lenderOrgId : userData?.investorOrgId) || '';
        return new Promise<void>((resolve, reject) => {
            dispatch(updateCompany({ orgId, customFields: customFieldsPayload }, { onSuccess: resolve, onError: reject }));
        });
    };

    // 8b. Update "Company Metrics" (Debt Products & capital requirements flow)
    const companyMetricsUpdate = async (navigationFunction: () => void) => {
        Mixpanel.track('Profile Updated', {
            PNOId: userData?.investorOrgId,
            userEmail: userData?.primaryEmail,
        });

        let orgId = '';
        if (urlParams.id) orgId = urlParams.id;
        else if (params.get('orgId')) orgId = params.get('orgId') as string;
        else if (dealDrawerState.companyId) orgId = dealDrawerState.companyId;
        else if (isLender) orgId = userData?.lenderOrgId as string;
        else if (userData?.accountType === 'ASSESSEE') orgId = userData?.assesseeOrgId;
        else if (isPartner) orgId = userOrgId;
        else orgId = userData?.investorOrgId;

        try {
            const metrics: any = unflatten(formData);
            const financials = metrics?.borrowerFinancials || {};

            const hasActualData = hasFinancialDataInNestedObject(financials.actualData);
            const hasDiscussionData = hasFinancialDataInNestedObject(financials.discussion);
            const hasAnyFinancialData = hasActualData || hasDiscussionData;

            if (hasAnyFinancialData && !financials.dataSource) {
                toast.error('Please select a Data Source for this entry');
                return;
            }

            // Remove entire borrowerFinancials object if no input data exists
            if (!hasAnyFinancialData && metrics.borrowerFinancials) delete metrics.borrowerFinancials;

            // For profile drawer, customFields are saved separately to OrganisationMaster via updateCompany
            if (source === PROFILE_BUTTON || source === ORG_USERS_TABLE) {
                delete metrics.customFields;
                if (metrics.profile) {
                    PROFILE_CUSTOM_FIELD_KEYS.forEach((key) => {
                        delete metrics.profile[key];
                    });
                }
            }

            await updateCompanyMetrics({
                orgId: orgId || (dealDrawerState.companyId as string),
                parentOrgId: userData?.investorOrgId,
                companyMetrics: metrics,
            });

            navigationFunction?.();
        } catch (err) {
            console.error(err);
        }
    };

    // 9. Create Deal
    const handleCreateDeal = () => {
        const payload: any = unflatten(formData);
        payload.dealProposerId = platformData?.investorOrgId;
        payload.dealBeneficiaryId = investorData?.dealDrawer?.companyId;
        payload.dealCreatedBy = loggedInUser?.userName;
        payload.owner = {
            name: loggedInUser?.userName,
            userId: getPlatformUserId(),
            activityBy: loggedInUser?.userName,
            activityDate: new Date().toISOString(),
            ownerType: DEFAULT_OWNER_TYPE,
        };
        if (payload?.dealTerms?.dealNotes) {
            payload.dealTerms.dealNotes = [
                {
                    note: payload.dealTerms.dealNotes,
                    noteBy: loggedInUser?.userName,
                    noteDate: new Date().toISOString(),
                },
            ];
        }
        dispatch(
            createDeal(payload, {
                onSuccess: () => {
                    closeDrawer();
                    navigate('/investor/app/deals', { state: { scrollPosition: 0 } });
                },
            }),
        );
    };

    // 10. Reset local storage
    const resetEditorDataInLocalStorage = () => {
        localStorage.removeItem('one_pager_content');
        localStorage.removeItem('one_pager_assessee');
    };

    // 11. Get toast messages,error messages, getInputValidation
    const getToastMessages = (source: string) => {
        switch (source) {
            case ORG_USERS_TABLE:
            case PROFILE_BUTTON:
                return {
                    success: 'Profile Details saved successfully',
                    error: 'Failed to update profile details',
                };
            case CREATE_INVESTMENT_PREFERENCE:
                return {
                    success: 'Investment Preferences created successfully',
                    error: 'Failed to create investment preference',
                };
            default:
                return {
                    success: 'Investment Preferences saved successfully',
                    error: 'Failed to update investment preference',
                };
        }
    };

    const getInputValidation = (value: string, key: string) => {
        switch (key) {
            case BORROWER_PROFILE_CONSTANTS.WEBSITE:
                return isWebsiteValid;
            case BORROWER_PROFILE_CONSTANTS.LINKEDIN:
                return isLinkedInValid || !value;
            default:
                return true;
        }
    };

    // -------------------------------------
    // HOOKS
    // -------------------------------------

    useWindowUnloadEffect(() => {
        resetEditorDataInLocalStorage();
    }, true);

    useEffect(() => {
        return () => {
            resetEditorDataInLocalStorage();
        };
    }, []);

    useEffect(() => {
        createMapOfRequiredVsSectionKeys();
    }, [formConfig]);

    useEffect(() => {
        checkCompleteForm();
    }, [formData, restrictedMap]);

    // Keep a ref to latest formData for use in the initialization timer
    const latestFormDataRef = useRef(formData);
    useEffect(() => {
        latestFormDataRef.current = formData;
    }, [formData]);

    // Mark form as initialized after data load is complete and a brief delay
    // This allows InputRenderer to set default values before we start tracking changes.
    // Note: 500ms delay accounts for React rendering cycle + InputRenderer useEffect execution.
    // This is a pragmatic approach; a more deterministic solution would require InputRenderer
    // to signal when all defaults are applied, but that adds significant complexity.
    useEffect(() => {
        // Don't initialize until data loading is complete and form config is available
        if (loadingPrefilledData || !formConfig || isFormInitialized) return;

        // Wait for InputRenderer default values to be applied, then snapshot the current formData
        const timer = setTimeout(() => {
            // Replace originalFormData with the current state after defaults are applied.
            // This ensures both server-loaded data AND default values are captured as the baseline.
            const currentFormData = latestFormDataRef.current;
            setOriginalFormData({ ...currentFormData });
            setIsFormInitialized(true);
        }, 500);
        return () => clearTimeout(timer);
    }, [loadingPrefilledData, formConfig, isFormInitialized]);

    useEffect(() => {
        // Don't track changes until form is fully initialized
        // This prevents false positives from default values being set by InputRenderer
        if (!isFormInitialized) {
            setHasUnsavedChanges(false);
            return;
        }

        // Normalize objects before comparison by removing empty/null/undefined values
        // This is needed because handleChange deletes keys with empty values,
        // but originalFormData may have those keys with empty string values
        const normalizeForComparison = (obj: Record<string, any>) => {
            const normalized: Record<string, any> = {};
            Object.entries(obj).forEach(([key, value]) => {
                // Only include non-empty values (treat empty string, null, undefined as "no value")
                if (value !== '' && value !== null && value !== undefined) {
                    normalized[key] = value;
                }
            });
            return normalized;
        };

        const normalizedFormData = normalizeForComparison(formData);
        const normalizedOriginalData = normalizeForComparison(originalFormData);
        const hasChanges = !isEqual(normalizedFormData, normalizedOriginalData);
        setHasUnsavedChanges(hasChanges);
    }, [formData, originalFormData, isFormInitialized]);

    useEffect(() => {
        validateForm();
    }, [step, formData, formConfig]);

    // Check for "Duplicate" param
    useEffect(() => {
        if (params.has('Duplicate')) {
            setDuplicate(true);
        }
    }, [params]);

    useEffect(() => {
        if (source === DEBT_PRODUCT_FILTERS) {
            setDisableSecondary(isEmpty(formData));
        } else {
            setDisableSecondary(step === 0 || !isWebsiteValid || !isLinkedInValid);
        }
    }, [formData, source, step]);

    useEffect(() => {
        if (duplicate) {
            params.delete('investmentPreferenceId');
            updateQueryParams(params, setParams, {});
        }
    }, [duplicate, params, setParams]);

    // Load the form config
    useEffect(() => {
        if (source === DEBT_PRODUCT_FILTERS) {
            setFilterType(investorData?.aicaDebtProductConfig?.filters?.filterType ?? '');
            setFormConfig(investorData?.aicaDebtProductConfig?.filters?.pages ?? []);
            setStepperConfig([]);
            return;
        }
        if (!platformData || !loggedInUser) return;
        const configName = investorData?.dealDrawer?.isPec
            ? AICA_INVESTMENT_PREFERENCE
            : source === 'Debt Products'
            ? AICA_COMPANY_PROFILE
            : source === PROFILE_BUTTON || source === ORG_USERS_TABLE
            ? investorData?.dealDrawer?.userType === USER_TYPE.PARTNER
                ? AICA_PARTNER_PROFILE
                : AICA_YOUR_PROFILE
            : AICA_CREATE_DEAL_DETAILS;
        getDealFormConfig(
            {
                accountType: investorData?.dealDrawer?.isLenderOrg ? USER_TYPES.LENDER : loggedInUser.accountType,
                investorOrgId: platformData?.investorOrgId,
                primaryEmail: loggedInUser?.primaryEmail,
                configName,
            },
            {
                onSuccess: (configData, orgName, displayOrgName) => {
                    if (orgName) setInvestorName(orgName);
                    let pages = configData?.pages ?? [];
                    let stepper = configData?.stepper ?? [];
                    if (configName === AICA_CREATE_DEAL_DETAILS && location.pathname.includes('/deals')) {
                        pages = pages.filter((page: any) => page.step !== CONFIG_STEP.SELECT_INVESTORS);
                        stepper = stepper.filter((item: any) => item.stepId !== CONFIG_STEP.SELECT_INVESTORS);
                    }
                    setFormConfig(pages);
                    setStepperConfig(stepper);
                    setDisplayOrgName(displayOrgName);
                },
            },
        );
        fetchCitiesList();
    }, [platformData, loggedInUser, investorData?.dealDrawer?.isPec, source]);

    // Fetch data, prefill, etc. (examples)
    // Example: if there's a dealId, getDealData, etc.
    useEffect(() => {
        if (!investorData?.dealDrawer?.dealId || investorData?.dealDrawer?.isPec) return;
        dispatch(
            getDealData(
                { dealId: investorData?.dealDrawer?.dealId },
                {
                    onSuccess: (dealData) => {
                        const dealNotes = dealData?.dealTerms?.dealNotes?.[0]?.note;
                        if (dealNotes) {
                            dealData.dealTerms.dealNotes = dealNotes;
                        }
                        const flattenedData = flatten(dealData);
                        setFormData(flattenedData);
                        setOriginalFormData(flattenedData);
                    },
                },
            ),
        );
    }, [investorData?.dealDrawer.dealId, investorData?.dealDrawer.isPec, dispatch]);

    useEffect(() => {
        if (!investorData?.dealDrawer?.lenderDealId) return;
        dispatch(
            getLenderDealData(
                { dealId: investorData?.dealDrawer?.lenderDealId },
                {
                    onSuccess: (dealData) => {
                        const dealNotes = dealData?.dealTerms?.dealNotes?.[0]?.note;
                        if (dealNotes) {
                            dealData.dealTerms.dealNotes = dealNotes;
                        }
                        const flattenedData = flatten(dealData);
                        setFormData(flattenedData);
                        setOriginalFormData(flattenedData);
                    },
                },
            ),
        );
    }, [investorData?.dealDrawer.lenderDealId, dispatch]);

    // If an existing PEC ID is in the query param
    useEffect(() => {
        if (!investorData?.dealDrawer?.isPec && source !== PROFILE_BUTTON && source !== ORG_USERS_TABLE) return;

        const prefId = investmentPreferenceId || params.get('investmentPreferenceId') || '';
        if (!prefId && source !== PROFILE_BUTTON && source !== ORG_USERS_TABLE) return;

        setInvestmentPreferenceId(prefId);

        // For regular PEC flow
        if (prefId && source !== PROFILE_BUTTON && source !== ORG_USERS_TABLE) {
            dispatch(
                getInvestmentPreference(
                    { investmentPreferenceId: prefId },
                    {
                        onSuccess: (data: any) => {
                            if (params.has('Duplicate')) {
                                data.preferredTerms = { ...data.preferredTerms, loanTypes: [] };
                            }
                            const flattenedData = flatten(data);
                            setFormData(flattenedData);
                            setInitialFormData(flattenedData);
                            setOriginalFormData(flattenedData);
                        },
                    },
                ),
            );
            return;
        } else if ((source === PROFILE_BUTTON || source === ORG_USERS_TABLE) && !loadingPrefilledData) {
            const assesseeOrgId =
                params.get('orgId') || (isPartner && userOrgId) || (isLender ? userData?.lenderOrgId : userData?.investorOrgId) || '';
            setLoadingPrefilledData(true);

            Promise.all([getCompanyMetrics({ assesseeOrgId }), fetchOrgInfoById({ orgId: assesseeOrgId })])
                .then(([metricsRes, orgInfo]) => {
                    const metricsData = metricsRes?.companyMetrics || metricsRes || {};
                    metricsData.profile = metricsData.profile || {};
                    metricsData.profile.organisationName = orgInfo?.orgName ?? '';
                    metricsData.profile.organisationDisplayName = orgInfo?.orgDisplayName ?? '';
                    metricsData.profile.feeShare = orgInfo?.customFields?.partnerFee ?? '';

                    // Map customFields from OrganisationMaster into profile section for the drawer
                    const orgCustomFields = orgInfo?.customFields || {};
                    PROFILE_CUSTOM_FIELD_KEYS.forEach((key) => {
                        if (orgCustomFields[key] !== undefined) {
                            metricsData.profile[key] = orgCustomFields[key];
                        }
                    });

                    if (source === PROFILE_BUTTON && (isLenderType(userData?.accountType) || isPartner)) {
                        setInvestorName(orgInfo?.orgDisplayName ? orgInfo?.orgDisplayName : orgInfo?.orgName);
                    }

                    const flattenedData = flatten(metricsData);
                    setFormData(flattenedData);
                    setInitialFormData(flattenedData);
                    setOriginalFormData(flattenedData);
                    setLoadingPrefilledData(false);
                })
                .catch((error) => {
                    console.error('Error fetching data:', error);
                    setLoadingPrefilledData(false);
                });
        }
    }, [investorData?.dealDrawer?.isPec, params, dispatch, source, userData, isLender, userOrgId]);

    useEffect(() => {
        if (
            (isLenderType(userData?.accountType) || isPartner) &&
            ([PROFILE_BUTTON].includes(source) || investorData?.dealDrawer?.isPec) &&
            displayOrgName
        ) {
            setInvestorName(displayOrgName);
        }
    }, [displayOrgName, source, userData?.accountType]);

    const fetchLoanTypes = async () => {
        const isDebtProducts = source === 'Debt Products' || formConfig?.[step - 1]?.step === CONFIG_STEP.DEAL_TERMS;
        let payload: any = {
            aggregatorId: userData?.investorOrgId,
        };
        if (investmentPreferenceId && !params.has('Duplicate')) {
            payload.investmentPreferenceId = investmentPreferenceId;
        }
        if (isLender) {
            payload.lenderId = userData?.lenderOrgId;
        }
        try {
            const response = await fetchAllAvailableProducts(payload, isDebtProducts);
            if (response) {
                setLoanTypeOptions(response);
            }
        } catch (error) {
            console.error('Error fetching loan types:', error);
        }
    };

    // LoanTypes, Sectors, etc.
    useEffect(() => {
        if (
            (investorData?.dealDrawer?.isPec && formConfig?.[step - 1]?.step === CONFIG_STEP.PREFERRED_TERMS) ||
            source === 'Debt Products' ||
            formConfig?.[step - 1]?.step === CONFIG_STEP.DEAL_TERMS
        ) {
            fetchLoanTypes();
            fetchNegativeLenders();
        }
    }, [formConfig?.[step - 1]]);

    // Build the name->id map once we have loanTypeOptions
    useEffect(() => {
        if (loanTypeOptions) {
            const productMap = new Map<string, string>();
            Object.values(loanTypeOptions).forEach((product: any) => {
                productMap.set(product.productName, product.productId);
            });
            setLoanTypeNameToIdMap(productMap);
        }
    }, [loanTypeOptions]);

    // Example: fetch Sectors
    useEffect(() => {
        const fetchCompanySector = async () => {
            try {
                const response = await fetchCompanySectorsList();
                if (response) {
                    setSectorList(response?.[AICA_GET_COMPANY_SECTOR_LIST]?.data?.positiveSector ?? []);
                    setNegativeSectorList(response?.[AICA_GET_COMPANY_SECTOR_LIST]?.data?.negativeSector ?? []);
                }
            } catch (error) {
                console.error('Error fetching company sectors:', error);
            }
        };
        const currentStep = formConfig?.[step - 1]?.step;
        const shouldFetch =
            currentStep === CONFIG_STEP.BORROWER_PROFILE ||
            currentStep === CONFIG_STEP.DEAL_TERMS ||
            source === PROFILE_BUTTON ||
            source === ORG_USERS_TABLE;
        if (!shouldFetch) return;
        fetchCompanySector();
    }, [formConfig, step, source]);

    useEffect(() => {
        if (!formData[BORROWER_PROFILE_CONSTANTS.WEBSITE]) {
            setIsWebsiteValid(true);
            setDisablePrimary(!isLinkedInValid);
            setDisableSecondary(!isLinkedInValid);
        }
    }, [formData]);

    const validateLinkedIn = (linkedin, isWebsiteValid) => {
        if (!linkedin) return { isValid: true, shouldDisable: !isWebsiteValid };

        const isValid = linkedInRegex.test(linkedin);

        return { isValid, shouldDisable: !isValid || !isWebsiteValid };
    };

    useEffect(() => {
        const linkedin = formData[BORROWER_PROFILE_CONSTANTS.LINKEDIN];
        const { isValid, shouldDisable } = validateLinkedIn(linkedin, isWebsiteValid);

        setIsLinkedInValid(isValid);
        setDisablePrimary(shouldDisable);
        setDisableSecondary(shouldDisable);
    }, [formData, isWebsiteValid]);

    // Watch for changes to borrowerFinancials data source in formData and sync with state
    // Support both dataSource (from config) and activeSource (from data API)
    useEffect(() => {
        const activeSourceValue = formData['borrowerFinancials.dataSource'];
        if (
            activeSourceValue &&
            (activeSourceValue === DATA_SOURCE_TYPE.ACTUAL_DATA || activeSourceValue === DATA_SOURCE_TYPE.DISCUSSION)
        ) {
            if (activeSourceValue !== activeFinancialsTab) {
                setActiveFinancialsTab(activeSourceValue);
            }
        }
    }, [formData['borrowerFinancials.dataSource']]);

    // Example: fetch default metrics if it's not PEC flow
    useEffect(() => {
        if (!platformData || !formConfig || investorData?.dealDrawer?.isPec) return;

        // If source = "Debt Products Filters"
        if (source === DEBT_PRODUCT_FILTERS) {
            // make a deep copy of debtProductFilters
            let payload = JSON.parse(JSON.stringify(debtProductFilters ?? {}));
            // remove filterType from payload.filters
            if (payload?.filters?.filterType) delete payload?.filters?.filterType;
            const flattenedData = flatten(payload);
            setFormData(flattenedData);
            setOriginalFormData(flattenedData);
            return;
        }
        // If source = "Debt Products"
        if (source === 'Debt Products' && !preFilledDataFetched) {
            setLoadingPrefilledData(true);
            getCompanyMetrics({
                assesseeOrgId: (urlParams.id as string) || user.userData.assesseeOrgId || (dealDrawerState.companyId as string),
                dataSource: DATA_SOURCE_TYPE.BOTH,
            })
                .then((response) => {
                    const data = response?.companyMetrics || response || {};
                    const borrowerProfile = data?.borrowerProfile ?? {};
                    const borrowerFinancials = data?.borrowerFinancials ?? {};

                    // Handle both dataSource (config) and activeSource (data API)
                    if (borrowerFinancials?.dataSource || borrowerFinancials?.activeSource) {
                        setActiveFinancialsTab(borrowerFinancials.dataSource || borrowerFinancials.activeSource);
                    }

                    if (borrowerProfile.website) {
                        setIsWebsitePrefilled(true);
                    } else {
                        setIsWebsitePrefilled(false);
                    }
                    // Fetch the deal metrics from Audited Financials
                    getCompanyDealMetrics({
                        requestPayload: {
                            requestId: getTimeStamp(),
                            parentOrgId: user.userData.investorOrgId,
                            orgId: urlParams.id as string,
                            type: 'auditedfinancials',
                            startDate: null,
                            endDate: null,
                            frequency: 'Yearly',
                            metricList: AUDITED_FINANCIAL_KEY_METRIC,
                            taskStepsList: ['AION_AUDITED_FINANCIAL'],
                            mis: 'Balance Sheet',
                            raw: true,
                            classificationId: 30,
                        },
                    })
                        .then((dealData) => {
                            setLoadingPrefilledData(false);

                            const borrowerFinancialData = dealData?.borrowerFinancials ?? {};
                            const borrowerProfileData = dealData?.borrowerProfile ?? {};
                            const financialYear = dealData?.financialYear || null;

                            // Auto the missing metrics in borrowerProfile with AICA Report data, else take it from the input metrics
                            const mergedBorrowerProfile = { ...borrowerProfile };

                            Object.keys(borrowerProfileData).forEach((key) => {
                                const isCompanyMetricsMissing =
                                    borrowerProfile[key] === undefined || borrowerProfile[key] === null || borrowerProfile[key] === '';
                                if (isCompanyMetricsMissing) {
                                    mergedBorrowerProfile[key] = borrowerProfileData[key];
                                }
                            });

                            const finalMetrics = {
                                ...data,
                                borrowerProfile: mergedBorrowerProfile,
                            };

                            if (financialYear) {
                                const newFinancialYearMapping = { ...auditedfinancialsMapping };
                                Object.keys(borrowerFinancialData).forEach((key) => {
                                    if (borrowerFinancialData[key] !== null) {
                                        newFinancialYearMapping[`borrowerFinancials.${key}`] = {
                                            // year: financialYear,
                                            value: borrowerFinancialData[key],
                                        };
                                    }
                                });
                                if (borrowerFinancials.latestConsolidatedFY !== undefined) {
                                    newFinancialYearMapping['borrowerFinancials.latestConsolidatedFY'] = {
                                        value: borrowerFinancials.latestConsolidatedFY,
                                    };
                                }
                                if (borrowerFinancials.latestStandaloneFY !== undefined) {
                                    newFinancialYearMapping['borrowerFinancials.latestStandaloneFY'] = {
                                        value: borrowerFinancials.latestStandaloneFY,
                                    };
                                }
                                Object.keys(borrowerProfileData).forEach((key) => {
                                    if (borrowerProfileData[key] !== null) {
                                        newFinancialYearMapping[`borrowerProfile.${key}`] = {
                                            // year: financialYear,
                                            value: borrowerProfileData[key],
                                        };
                                    }
                                });

                                // Add Year to the mapping for audited financials
                                newFinancialYearMapping['borrowerFinancials.dataAsOfDate'] = {
                                    value: `${FY_END_DATE} ${(
                                        getFinancialYear(Number(getAuditedYear(financialYear)), false, true) ?? ''
                                    ).toString()}`,
                                };
                                newFinancialYearMapping['financialYear'] = {
                                    value: (getFinancialYear(Number(financialYear), true) ?? '').toString(),
                                };
                                setAuditedfinancialsMapping(newFinancialYearMapping);
                            }
                            const flattenedData = flatten(finalMetrics);
                            setFormData(flattenedData);
                            setOriginalFormData(flattenedData);
                            setPreFilledDataFetched(true);
                        })
                        .catch((error) => {
                            console.error('Error fetching deal data:', error);
                            setLoadingPrefilledData(false);
                        });
                })
                .catch((error) => {
                    console.error('Error fetching initial data:', error);
                    setLoadingPrefilledData(false);
                });
        }
        // if normal deal flow, e.g. step = BORROWER_PROFILE
        else if (
            formConfig?.[step - 1]?.step === CONFIG_STEP.BORROWER_PROFILE &&
            isEmpty(investorData?.dealDrawer?.dealId) &&
            isEmpty(investorData?.dealDrawer?.lenderDealId) &&
            !preFilledDataFetched
        ) {
            const [from, to] = initializeDatepickerDates('Yearly');
            setLoadingPrefilledData(true);
            dispatch(
                getMetricsForDealCreation(
                    {
                        parentOrgId: platformData.investorOrgId,
                        orgId: investorData?.dealDrawer?.companyId as string,
                        startDate: from,
                        endDate: to,
                    },
                    {
                        onSuccess: (metricsData) => {
                            setLoadingPrefilledData(false);
                            const flattenedData = flatten(metricsData);
                            setFormData(flattenedData);
                            setOriginalFormData(flattenedData);
                            setPreFilledDataFetched(true);
                        },
                        onError: () => {
                            setLoadingPrefilledData(false);
                        },
                    },
                ),
            );
        }

        // If step = SELECT_INVESTORS
        // else if (formConfig?.[step - 1]?.step === CONFIG_STEP.SELECT_INVESTORS) {
        //     const createDealPayload: any = unflatten(formData);
        //     createDealPayload.dealProposerId = platformData?.investorOrgId;
        //     createDealPayload.dealBeneficiaryId = dealDrawerState?.companyId;
        //     createDealPayload.dealCreatedBy = loggedInUser?.userName;
        //     if (createDealPayload.dealTerms?.dealNotes) {
        //         createDealPayload.dealTerms.dealNotes = [
        //             {
        //                 note: createDealPayload.dealTerms.dealNotes,
        //                 noteBy: loggedInUser?.userName,
        //                 noteDate: new Date().toISOString(),
        //             },
        //         ];
        //     }
        //     dispatch(
        //         getPecMatchingData(createDealPayload, {
        //             onSuccess: (pecMatchingData) => {
        //                 setLenderList(pecMatchingData);
        //             },
        //         }),
        //     );
        // }
    }, [
        formConfig,
        step,
        investorData?.dealDrawer,
        debtProductFilters,
        source,
        preFilledDataFetched,
        platformData,
        loggedInUser,
        dispatch,
        urlParams,
    ]);

    // Optional for animation class
    useEffect(() => {
        if (animationClass) {
            const timer = setTimeout(() => {
                setAnimationClass('fade-in');
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [animationClass]);

    // -------------------------------------
    // RETURN HOOK DATA
    // -------------------------------------
    return {
        // State
        formData,
        setFormData,
        step,
        disablePrimary,
        disableSecondary,
        animationClass,
        loadingPrefilledData,
        formConfig,
        stepperConfig,
        investorName,
        lenderList,
        loanTypeOptions,
        sectorList,
        negativeSectorList,
        dealDrawerState: investorData?.dealDrawer,
        isFromComplete,
        source,
        isWebsiteValid,
        setIsWebsiteValid,
        isLinkedInValid,
        isWebsitePrefilled,
        showDismissalAlert,
        hasUnsavedChanges,
        activeFinancialsTab,

        // PEC field selection
        pecStepSection,
        pecFilteredSection,
        pecPickerOptions,
        pecSelectedFields,
        pecAlwaysOnFields,
        isStepConfigured,

        // Refs
        dealDrawerBodyRef,
        stepContainerRef,

        // Methods
        closeDrawer,
        handlePrimary,
        handleSecondary,
        handleStepperClick,
        setDisablePrimary,
        handleChange,
        handlePickerSelectionChange,
        handleClearField,
        handleRemoveAllForStep,
        handleClearAllForStep,
        auditedfinancialsMapping,
        getInputValidation,
        getButtonIcon,
        getButtonText,
        validateWebsite,
        negativeLenderList,
        connectedLenderNames,
        getConvertedValue,
        navigateStep,
        companyMetricsUpdate,
        continueToNextStep,
        params,
        setParams,
        handleUpdateMandate,
        handleSkipMandate,
        handleDrawerClose,
        handleSaveAndClose,
        handleCloseWithoutSave,
        setShowDismissalAlert,
        citiesList,
        handleFinancialsTabChange,
        showRemoveAllConfirm,
        setShowRemoveAllConfirm,
        isPickerOpen,
        setIsPickerOpen,
    };
}
